import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import {
	DataPerformanceSummaryRequestBodyObjectType,
	DataPerformanceSummaryType,
} from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import type { MetricSetup } from 'webapp/models-custom';
import type { DataPerformanceSummaryObject as DataPerformanceSummaryObjectBase } from 'webapp/models-custom/data-performance-summary/base';
import DataPerformanceSummaryBase from 'webapp/models-custom/data-performance-summary/base';

export interface DataPerformanceSummaryObject extends DataPerformanceSummaryObjectBase {
	objectType: DataPerformanceSummaryType.BLOCK;
	previewHash?: string;
}

export default class DataPerformanceSummaryBlock extends DataPerformanceSummaryBase {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;

	objectType = DataPerformanceSummaryType.BLOCK;
	previewHash = ''; // Has value for block in board preview

	constructor(model: DataPerformanceSummaryObject) {
		super(model);

		if (model.previewHash) {
			this.previewHash = model.previewHash;
		}
	}

	@cached
	get dateRangeFormula() {
		return this.object?.dateRange;
	}

	@cached
	get object() {
		if (this.objectId) {
			return this.previewHash
				? this.mmBlockPreviewsService.findModel(this.objectId)
				: this.mmBlocksService.findModel(this.objectId);
		}

		return null;
	}

	get title() {
		const mso = this.oneMetricSetup;
		return (mso?.metric ? mso.selectedMetricTitle : this.object?.name) || '';
	}

	@cached
	get sources() {
		if (this.object) {
			return this.object.sourcesObjs;
		}

		return [];
	}

	get trend() {
		return this.sanitizedSummary?.trend || this.defaultTrend;
	}

	get isDataIncomplete() {
		return undefined;
	}

	get isDataEmpty() {
		return false;
	}

	get trackingObjectType() {
		return this.singleOutputMetricSetupData ? 'metric' : 'multiple_metric';
	}

	get trackingMetricsNames() {
		const object = this.object;

		if (object) {
			return this.mmDataPerformanceSummariesService.getTrackingMetricNamesFromBlock(object);
		}

		return [];
	}

	get summariesRequestBodyBase(): APIData.GenerativeAI.DataPerformance.SummariesRequest | null {
		if (this.object?.numericId) {
			return {
				typeObject: DataPerformanceSummaryRequestBodyObjectType.BLOCK,
				typeObjectId: this.object.numericId,
			};
		}

		return null;
	}

	@cached
	private get oneMetricSetup() {
		const object = this.object;

		// returns null if there are multiple different metrics on block

		let metricSetup: MetricSetup | null = null;

		if (object) {
			const allDataSelects = this.object.dataSelects;
			for (const ds of allDataSelects) {
				if (!metricSetup) {
					metricSetup = ds.metricSetup.createCopy();
				} else if (!metricSetup.isMatchingMetricSetup(ds.metricSetup)) {
					metricSetup = null;
					break;
				}
			}
		}

		return metricSetup;
	}

	@cached
	get metricSetupData() {
		return this.oneMetricSetup?.metricSetupData || null;
	}

	@cached
	get singleOutputMetricSetupData() {
		if (this.oneMetricSetup?.isSingleOutput) {
			return this.metricSetupData;
		}

		return null;
	}

	/* private get objectDateRange(): DateRange | null {
		if (this.object?.dateRange) {
			return DateRange.createFromFormula(this.object.dateRange);
		}

		return null;
	} */

	resetTrend() {
		if (this.summaries.length) {
			const summaries = copyObject(this.summaries);
			summaries[0]!.trend = this.defaultTrend;
			this.summaries = summaries;
		}
	}
}
